import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, OverlayViewF, OVERLAY_MOUSE_TARGET, useJsApiLoader } from '@react-google-maps/api';
import { format, parseISO } from 'date-fns';
import type { ItineraryDay, TripPhoto } from '../../types';
import { placesApi } from '../../api/places.api';
import { LIBRARIES, MAPS_API_KEY } from '../../utils/mapConstants';
import { useDateLocale } from '../../utils/dateLocale';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import { photoPlaces, type PhotoPlace } from '../../utils/journalViews';
import { StopPhotoStrip } from '../trips/itinerary/StopPhotoStrip';
import LoadingScreen from '../ui/LoadingScreen';
import EmptyState from '../ui/EmptyState';
import { Icon, Map as MapIcon, X } from '../ui/Icon';

const PIN = 44;

interface Props {
  tripId: string;
  days: ItineraryDay[];
  photos: TripPhoto[];
  onOpenPhoto: (photos: TripPhoto[], index: number) => void;
}

/**
 * The photos on the map, each on its stop (ADR-0042). Coordinates come from the
 * itinerary already loaded: nothing here resolves a place. The map itself is a
 * Google load, measured like every other (ADR-0001).
 */
export default function JournalPhotoMap({ tripId, days, photos, onOpenPhoto }: Props) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY ?? '', id: 'google-map-script', libraries: LIBRARIES });
  const { places, unplaced } = useMemo(() => photoPlaces(photos, days), [photos, days]);
  // Google validates a LatLngLiteral strictly: a place with its stop and photos
  // attached is "unknown property stop" and the whole tab crashes. Bare coords only.
  const points = useMemo(() => places.map(p => ({ lat: p.lat, lng: p.lng })), [places]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = places.find(p => p.stop.id === selectedId) ?? null;
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || points.length === 0) return;
    if (points.length === 1) { map.setCenter(points[0]); map.setZoom(13); return; }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend(p));
    map.fitBounds(bounds, 60);
  }, [points, mapReady]);

  if (places.length === 0) {
    return <EmptyState size="inline" icon={MapIcon} title={t('journal.map.empty')} body={t('journal.map.emptyBody')} />;
  }
  if (!MAPS_API_KEY) return <p className="text-sm text-slate-400">{t('journal.map.unavailable')}</p>;
  if (!isLoaded) return <LoadingScreen />;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800 flex-1 min-h-[360px]">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={points[0]}
        zoom={8}
        onLoad={m => { mapRef.current = m; setMapReady(true); placesApi.reportMapLoad(tripId); }}
        onClick={() => setSelectedId(null)}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, clickableIcons: false }}
      >
        {places.map(place => (
          <PhotoPin key={place.stop.id} place={place} active={place.stop.id === selectedId} onClick={() => setSelectedId(place.stop.id)} />
        ))}
      </GoogleMap>

      {selected ? (
        <div className="absolute left-3 right-3 bottom-3 rounded-2xl bg-white dark:bg-slate-900 shadow-lg p-3 pr-10 animate-fade-in">
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">
            {format(parseISO(selected.day.date.slice(0, 10)), 'EEEE d MMMM', { locale })} · {t('gallery.dayOption', { number: selected.day.dayNumber })}
          </p>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{selected.stop.title}</p>
          <StopPhotoStrip photos={selected.photos} onOpen={i => onOpenPhoto(selected.photos, i)} />
          <button
            onClick={() => setSelectedId(null)}
            className="absolute top-2 right-2 btn-icon w-7 h-7 text-slate-400"
            aria-label={t('common.close')}
          >
            <Icon as={X} size={14} />
          </button>
        </div>
      ) : unplaced > 0 && (
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white dark:bg-slate-900 shadow text-xs text-slate-500">
          {t('journal.map.unplaced', { count: unplaced })}
        </span>
      )}
    </div>
  );
}

function PhotoPin({ place, active, onClick }: { place: PhotoPlace; active: boolean; onClick: () => void }) {
  const cover = place.photos[0];
  return (
    <OverlayViewF
      position={{ lat: place.lat, lng: place.lng }}
      mapPaneName={OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={() => ({ x: -PIN / 2, y: -PIN / 2 })}
      zIndex={active ? 900 : undefined}
    >
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onClick(); }}
        title={place.stop.title}
        className={`relative block rounded-full border-[3px] shadow-md cursor-pointer transition-transform ${active ? 'border-teal-500 scale-110' : 'border-white hover:scale-105'}`}
        style={{ width: PIN, height: PIN }}
      >
        <img src={tripPhotoUrl(cover.thumbnailFilename ?? cover.filename)} alt="" className="w-full h-full rounded-full object-cover" draggable={false} />
        {place.photos.length > 1 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center">
            {place.photos.length}
          </span>
        )}
      </button>
    </OverlayViewF>
  );
}
